import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { DBG_ENABLED } from '../../utils/dbgFlag';
import { side, NSLOT } from '../../utils/sideProbe';
import { sync } from '../../utils/syncProbe';
import { computeSliceShift } from '../../utils/sliceShift';
import { positionFor } from '../../utils/anchoredNav';
import type { UploadedBatch } from '../../utils/uploadedBatch';
import type { GpuCopy } from '../../utils/sideClip';

// ---------------------------------------------------------------------------
// Sonda da lateral e do chão (?dbg=1). Veja src/utils/sideProbe.ts.
// Só LÊ. Sem a flag retorna antes de registrar qualquer hook (a flag é constante de módulo).
// O useFrame roda depois do recorte da lateral (ordem de registro) e prepara a janela visível do quadro;
// a medição em si acontece no onBeforeRender de cada linha, imediatamente antes do desenho.
// ---------------------------------------------------------------------------

type LineRef = { current: THREE.Line | null };
export interface SideProbeArgs {
  lines: LineRef[];                       // 0 lateral, 1-3 SMAs da lateral, 4 chão, 5-7 SMAs do chão
  drawRef: { current: UploadedBatch | null };
  historyOffsetRef: { current: number } | undefined;
  startX: number; backChartWidth: number; H: number;
  showSide: boolean; showFloor: boolean; sma: [boolean, boolean, boolean];
  /** estado do recorte da lateral (contagem de pontos válidos da cópia da GPU), linhas 0-3 */
  gpu?: { current: GpuCopy[] | null };
}

export function useSideProbe(a: SideProbeArgs): void {
  if (!DBG_ENABLED) return;
  const P = side!;
  const handlers = useRef<((...args: any[]) => void)[] | null>(null);
  if (handlers.current === null) {
    handlers.current = [];
    for (let s = 0; s < NSLOT; s++) {
      const slot = s;
      handlers.current.push((r: any, _sc: any, _c: any, geometry: any, material: any) => P.draw(slot, geometry, material, r));
    }
  }
  const last = useRef({ wall: NaN, main: NaN, data: null as any, wallData: null as any, sw: 0 });

  useFrame(() => {
    const now = performance.now();
    P.frame(now);
    const U = a.drawRef.current;
    P.mode = sync ? sync.curMode : 4;
    if (!U) return;
    // troca de lote (ou dados novos) neste quadro: salto de contagem é esperado por até 2 quadros
    const L = last.current;
    if (L.wall !== U.wallSliceOffset || L.main !== U.sliceOffset || L.data !== U.data || L.wallData !== U.wallData) L.sw = 2;
    L.wall = U.wallSliceOffset; L.main = U.sliceOffset; L.data = U.data; L.wallData = U.wallData;
    P.loteSwitch = L.sw > 0; if (L.sw > 0) L.sw--;
    // posição relativa a cada lote (F2), a mesma do recorte e do corte do shader
    const shW = computeSliceShift(positionFor(a.historyOffsetRef, U.wallNewestAbs), U.wallSliceOffset, U.wallPadLeft, U.wallPadRight) * U.wallStepX;
    const shM = computeSliceShift(positionFor(a.historyOffsetRef, U.newestAbs), U.sliceOffset, U.padLeft, U.padRight) * U.stepX;
    P.xLoWall = a.startX - shW; P.xHiWall = a.startX + a.backChartWidth - shW;
    P.xLoMain = a.startX - shM; P.xHiMain = a.startX + a.backChartWidth - shM;
    P.H = a.H; P.stepWall = U.wallStepX || 1; P.stepMain = U.stepX || 1;
    P.loteWall = U.wallSliceOffset; P.loteMain = U.sliceOffset;
    const nW = U.wallSmoothedPtsCount, n10 = U.sma10Pts.length / 3, n20 = U.sma20Pts.length / 3, n50 = U.sma50Pts.length / 3;
    P.nExp[0] = nW; P.nExp[1] = n10; P.nExp[2] = n20; P.nExp[3] = n50;
    P.nExp[4] = nW; P.nExp[5] = n10; P.nExp[6] = n20; P.nExp[7] = n50;
    // contagem válida da cópia da GPU (a mesma que o recorte usa) e testemunha de L8 (recorte antigo x GPU)
    const G = a.gpu ? a.gpu.current : null;
    for (let s = 0; s < 4; s++) {
      const st = G ? G[s] : null, o = a.lines[s].current;
      P.nGpuValid[s] = st && st.attr && o && st.attr === o.geometry.attributes.position ? st.n : -1;
      if (!a.showSide || !o || !st || P.nGpuValid[s] < 2) continue;
      if (s === 0) { if (U.wallSmoothedPoints instanceof Float32Array) P.witnessL8(0, U.wallSmoothedPoints, nW, st.attr.array, st.n); }
      else if (a.sma[s - 1]) {
        const u = s === 1 ? U.sma10Pts : s === 2 ? U.sma20Pts : U.sma50Pts;
        P.witnessL8(s, u, u.length / 3, st.attr.array, st.n);
      }
    }
    const H = handlers.current!;
    for (let s = 0; s < NSLOT; s++) {
      const o = a.lines[s].current;
      const on = s < 4 ? a.showSide : a.showFloor;
      const smaOn = (s & 3) === 0 ? true : a.sma[(s & 3) - 1];
      P.want[s] = o && o.parent && on && smaOn && o.visible && P.nExp[s] >= 2 ? 1 : 0; // na cena e com pontos
      if (o && P.attach(s, o)) (o as any).onBeforeRender = H[s];
    }
  });
}
